// Assignment Recommendation Utilities

import type { Employee, ResourceAllocation } from '@/types';
import type {
  PersonRecommendation,
  PersonAvailability,
  SkillMatch,
  ProjectConflict,
  AvailabilityStatus,
} from '@/types/assignment.types';

/**
 * Calculate the number of months between two dates (inclusive of both start and end months).
 * E.g. Jun 2026 to Sep 2027 = 16 months.
 */
function calculateMonthsDuration(start: Date, end: Date): number {
  const startMonth = start.getFullYear() * 12 + start.getMonth();
  const endMonth = end.getFullYear() * 12 + end.getMonth();
  return Math.max(1, endMonth - startMonth + 1);
}

/**
 * Default availability window used when a project has no start and/or end date:
 * the current calendar year, matching the 12-month window the budget screens fall
 * back to.
 */
export function getDefaultProjectDateRange(reference: Date = new Date()): {
  startDate: string;
  endDate: string;
} {
  const year = reference.getFullYear();
  return { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
}

/**
 * Calculate the number of overlapping months between two date ranges (inclusive).
 * Returns 0 if there is no overlap.
 */
function calculateOverlapMonths(
  rangeAStart: Date, rangeAEnd: Date,
  rangeBStart: Date, rangeBEnd: Date
): number {
  const overlapStartMs = Math.max(rangeAStart.getTime(), rangeBStart.getTime());
  const overlapEndMs = Math.min(rangeAEnd.getTime(), rangeBEnd.getTime());
  if (overlapStartMs > overlapEndMs) return 0;

  const overlapStart = new Date(overlapStartMs);
  const overlapEnd = new Date(overlapEndMs);
  return calculateMonthsDuration(overlapStart, overlapEnd);
}

/**
 * Calculate person availability based on their working pattern and
 * hours already assigned to other projects during the same time frame.
 *
 * Formula:
 *   Monthly Availability = (hoursPerWeek * 52) / 12
 *   Total Availability   = Monthly Availability * Project Duration (months)
 *   Hours on other projects = sum of overlapping monthly hours from other assignments
 *   Resource Availability = Total Availability - Hours on other projects  (rounded to integer)
 */
export function calculatePersonAvailability(
  person: Employee,
  allocations: ResourceAllocation[],
  projectStartDate: string,
  projectEndDate: string,
  currentProjectId?: number
): PersonAvailability {
  const hoursPerWeek = person.hoursPerWeek ?? 40;
  const monthlyAvailability = (hoursPerWeek * 52) / 12;

  const defaults = getDefaultProjectDateRange();
  const start = new Date(projectStartDate || defaults.startDate);
  const end = new Date(projectEndDate || defaults.endDate);

  const projectMonths = calculateMonthsDuration(start, end);
  const totalCapacity = monthlyAvailability * projectMonths;

  // Find allocations for this person on ALL projects that overlap the project dates
  const overlappingAllocations = allocations.filter(alloc => {
    if (alloc.employeeId !== person.id) return false;
    const allocStart = new Date(alloc.startDate);
    const allocEnd = new Date(alloc.endDate);
    return allocStart <= end && allocEnd >= start;
  });

  // Calculate total hours already assigned across all projects during the overlap
  const allocatedHours = overlappingAllocations.reduce((sum, alloc) => {
    const allocStart = new Date(alloc.startDate);
    const allocEnd = new Date(alloc.endDate);
    const allocDurationMonths = calculateMonthsDuration(allocStart, allocEnd);
    const overlapMonths = calculateOverlapMonths(start, end, allocStart, allocEnd);

    if (alloc.allocatedHours != null && alloc.allocatedHours > 0) {
      // Distribute total hours evenly across the allocation's months
      const monthlyRate = alloc.allocatedHours / allocDurationMonths;
      return sum + monthlyRate * overlapMonths;
    }

    if (alloc.allocationPercent != null && alloc.allocationPercent > 0) {
      const monthlyHours = monthlyAvailability * (alloc.allocationPercent / 100);
      return sum + monthlyHours * overlapMonths;
    }

    return sum;
  }, 0);

  const availableHours = Math.max(0, Math.round(totalCapacity - allocatedHours));
  const utilisationPercent =
    totalCapacity > 0 ? Math.round((allocatedHours / totalCapacity) * 100) : 0;

  let status: AvailabilityStatus;
  if (availableHours <= 0) {
    status = 'unavailable';
  } else if (utilisationPercent >= 70) {
    status = 'partially-available';
  } else {
    status = 'available';
  }

  // Map conflicts
  const conflicts: ProjectConflict[] = overlappingAllocations.map(alloc => ({
    projectId: alloc.projectId,
    projectName: alloc.projectName ?? `Project ${alloc.projectId}`,
    allocatedHours: alloc.allocatedHours ?? (alloc.allocationPercent ?? 0),
    startDate: alloc.startDate,
    endDate: alloc.endDate,
  }));

  return {
    status,
    availableHours,
    totalCapacity: Math.round(totalCapacity),
    utilisationPercent,
    conflicts,
  };
}

/**
 * Calculate skills match between person and required skills
 */
export function calculateSkillsMatch(
  personSkills: string[],
  requiredSkills: string[]
): { matches: SkillMatch[]; matchPercent: number } {
  if (!requiredSkills || requiredSkills.length === 0) {
    return { matches: [], matchPercent: 100 };
  }
  
  const safePersonSkills = personSkills ?? [];
  const matches: SkillMatch[] = requiredSkills.map(skill => ({
    skill,
    required: true,
    hasSkill: safePersonSkills.some(ps => 
      ps.toLowerCase().includes(skill.toLowerCase()) || 
      skill.toLowerCase().includes(ps.toLowerCase())
    ),
  }));
  
  const matchedCount = matches.filter(m => m.hasSkill).length;
  const matchPercent = Math.round((matchedCount / requiredSkills.length) * 100);
  
  return { matches, matchPercent };
}

/**
 * Calculate suitability score for a person
 */
export function calculateSuitabilityScore(
  person: Employee,
  availability: PersonAvailability,
  skillsMatchPercent: number,
  requiredHours: number,
  roleName: string
): number {
  let score = 0;
  
  // Skills match (40% weight)
  score += skillsMatchPercent * 0.4;
  
  // Availability (30% weight)
  const availabilityScore = Math.min(100, (availability.availableHours / requiredHours) * 100);
  score += availabilityScore * 0.3;
  
  // Role match (20% weight)
  const roleMatch = person.role.toLowerCase().includes(roleName.toLowerCase()) ||
                    roleName.toLowerCase().includes(person.role.toLowerCase());
  score += (roleMatch ? 100 : 50) * 0.2;
  
  // Utilisation (10% weight) - prefer people with moderate utilisation
  const utilisationScore = availability.utilisationPercent < 50 ? 100 : 
                          availability.utilisationPercent < 80 ? 80 : 40;
  score += utilisationScore * 0.1;
  
  return Math.round(score);
}

/**
 * Generate rationale for recommendation
 */
export function generateRationale(
  person: Employee,
  availability: PersonAvailability,
  skillsMatchPercent: number,
  requiredHours: number,
  roleName: string
): string[] {
  const rationale: string[] = [];
  
  // Availability
  if (availability.status === 'available') {
    rationale.push(`Fully available (${availability.availableHours}h capacity)`);
  } else if (availability.status === 'partially-available') {
    rationale.push(`Partially available (${availability.availableHours}h of ${requiredHours}h needed)`);
  }
  
  // Role match
  if (person.role.toLowerCase().includes(roleName.toLowerCase())) {
    rationale.push(`Exact role match: ${person.role}`);
  } else if (roleName.toLowerCase().includes(person.role.toLowerCase())) {
    rationale.push(`Related role: ${person.role}`);
  }
  
  // Experience indicators
  if (person.role.toLowerCase().includes('senior') || person.role.toLowerCase().includes('lead')) {
    rationale.push('Senior-level experience');
  }
  
  return rationale;
}

/**
 * Generate warnings for recommendation
 */
export function generateWarnings(
  availability: PersonAvailability,
  skillsMatchPercent: number,
  requiredHours: number
): string[] {
  const warnings: string[] = [];
  
  // Availability warnings
  if (availability.status === 'unavailable') {
    warnings.push('Currently fully allocated to other projects');
  } else if (availability.availableHours < requiredHours) {
    const shortfall = requiredHours - availability.availableHours;
    warnings.push(`${shortfall}h short of required hours`);
  }
  
  // Conflict warnings
  if (availability.conflicts.length > 0) {
    warnings.push(`${availability.conflicts.length} project conflict(s)`);
  }
  
  // Skills warnings
  if (skillsMatchPercent < 50) {
    warnings.push('Limited skills match - may require training');
  }
  
  // Over-utilisation warning
  if (availability.utilisationPercent >= 90) {
    warnings.push('Near capacity - limited flexibility');
  }
  
  return warnings;
}

/**
 * Generate recommendations for a role
 */
export function generateRecommendations(
  allPeople: Employee[],
  allocations: ResourceAllocation[],
  roleName: string,
  requiredHours: number,
  requiredSkills: string[],
  projectId: number,
  projectStartDate: string,
  projectEndDate: string
): PersonRecommendation[] {
  const recommendations: PersonRecommendation[] = allPeople
    .filter(person => person.isAvailable !== false) // Filter out explicitly unavailable people
    .filter(person => person.role === roleName) // Only show people with matching role
    .map(person => {
      const availability = calculatePersonAvailability(
        person,
        allocations,
        projectStartDate,
        projectEndDate,
        projectId
      );
      
      const { matches: skillsMatch, matchPercent: skillsMatchPercent } = calculateSkillsMatch(
        person.skills,
        requiredSkills
      );
      
      const suitabilityScore = calculateSuitabilityScore(
        person,
        availability,
        skillsMatchPercent,
        requiredHours,
        roleName
      );
      
      const rationale = generateRationale(
        person,
        availability,
        skillsMatchPercent,
        requiredHours,
        roleName
      );
      
      const warnings = generateWarnings(
        availability,
        skillsMatchPercent,
        requiredHours
      );
      
      return {
        person,
        availability,
        suitabilityScore,
        skillsMatch,
        skillsMatchPercent,
        rationale,
        warnings,
        rank: 0, // Will be set after sorting
      };
    })
    .filter(rec => rec.availability.availableHours >= requiredHours)
    .sort((a, b) => b.suitabilityScore - a.suitabilityScore)
    .map((rec, index) => ({
      ...rec,
      rank: index + 1,
    }));
  
  return recommendations;
}

/**
 * Sentinel filter value representing people who have no department assigned.
 */
export const UNASSIGNED_DEPARTMENT = '__unassigned__';

/**
 * Whether a department value should be treated as "Unassigned" (blank/empty or
 * the literal "Unassigned" label the backend may return).
 */
export function isUnassignedDepartment(department: string | null | undefined): boolean {
  const value = (department ?? '').trim();
  return value === '' || value.toLowerCase() === 'unassigned';
}

/**
 * Build the list of department filter options from the available people.
 * Named departments are listed alphabetically; a single "Unassigned" option is
 * appended when any person has no department assigned.
 */
export function getDepartmentFilterOptions(people: Employee[]): Array<{ value: string; label: string }> {
  const named = getUniqueValues(
    people
      .map(p => p.department)
      .filter(dept => dept && dept.trim() !== '' && dept.trim().toLowerCase() !== 'unassigned')
  ).sort();

  const options = named.map(dept => ({ value: dept, label: dept }));

  if (people.some(p => isUnassignedDepartment(p.department))) {
    options.push({ value: UNASSIGNED_DEPARTMENT, label: 'Unassigned' });
  }

  return options;
}

/**
 * Filter people based on criteria
 */
export function filterPeople(
  people: Employee[],
  searchQuery: string,
  roles: string[],
  departments: string[],
  availabilityStatuses: AvailabilityStatus[],
  availabilityMap: Map<number, PersonAvailability>
): Employee[] {
  return people.filter(person => {
    // Search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        person.name.toLowerCase().includes(query) ||
        person.role.toLowerCase().includes(query) ||
        person.email.toLowerCase().includes(query) ||
        (person.skills ?? []).some(skill => skill.toLowerCase().includes(query));
      
      if (!matchesSearch) return false;
    }
    
    // Role filter
    if (roles.length > 0 && !roles.includes(person.role)) {
      return false;
    }
    
    // Department filter
    if (departments.length > 0) {
      const matchesDepartment = departments.some(dept =>
        dept === UNASSIGNED_DEPARTMENT
          ? isUnassignedDepartment(person.department)
          : dept === person.department
      );
      if (!matchesDepartment) return false;
    }
    
    // Only show resources with at least some availability
    const availability = availabilityMap.get(person.id);
    if (!availability || availability.availableHours <= 0) {
      return false;
    }

    // Availability filter
    if (availabilityStatuses.length > 0) {
      if (!availabilityStatuses.includes(availability.status)) {
        return false;
      }
    }
    
    return true;
  });
}

/**
 * Get unique values from array
 */
export function getUniqueValues<T>(array: T[]): T[] {
  return Array.from(new Set(array));
}

/**
 * Format hours for display
 */
export function formatHours(hours: number): string {
  return `${hours.toFixed(0)}h`;
}

/**
 * Get availability status color
 */
export function getAvailabilityColor(status: AvailabilityStatus): string {
  switch (status) {
    case 'available':
      return 'text-green-600';
    case 'partially-available':
      return 'text-amber-600';
    case 'unavailable':
      return 'text-red-600';
    default:
      return 'text-gray-600';
  }
}

/**
 * Get availability status badge variant
 */
export function getAvailabilityBadgeVariant(status: AvailabilityStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'available':
      return 'default';
    case 'partially-available':
      return 'secondary';
    case 'unavailable':
      return 'destructive';
    default:
      return 'outline';
  }
}

/**
 * Get suitability score color
 */
export function getSuitabilityColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-amber-600';
  return 'text-red-600';
}

/**
 * Get suitability score label
 */
export function getSuitabilityLabel(score: number): string {
  if (score >= 90) return 'Excellent Match';
  if (score >= 80) return 'Very Good Match';
  if (score >= 70) return 'Good Match';
  if (score >= 60) return 'Fair Match';
  return 'Poor Match';
}
